import React, { useState } from 'react';
import SvgIcons from '../../../../../services/SvgIcons';
import './CreatePasswordForm.scss';
import { checkCapital, checkDigit, checkLowercase, checkSpecialCharacter } from '../../../../../services/inputCheck';
import { createPasswordService } from '../../../../../services/api/createPasswordService';

const CreatePasswordForm = ({ createToken, onSuccess }) => {
    const [formData, setFormData] = useState({
        createToken: createToken,
        newPassword: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        general: ''
    });

    const [hasGoodLength, setHasGoodLength] = useState(false);
    const [hasCapitalLetter, setHasCapitalLetter] = useState(false);
    const [hasLowercaseLetter, setHasLowercaseLetter] = useState(false);
    const [hasDigit, setHasDigit] = useState(false);
    const [hasSpecialCharacter, setHasSpecialCharacter] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [enabledValidateBtn, setEnabledValidateBtn] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });

        // Validation en temps réel
        if (name === 'newPassword') {
            e.target.value.length >= 8 && e.target.value.length <= 20 ? setHasGoodLength(true) : setHasGoodLength(false);
            checkCapital(e.target.value) ? setHasCapitalLetter(true) : setHasCapitalLetter(false);
            checkLowercase(e.target.value) ? setHasLowercaseLetter(true) : setHasLowercaseLetter(false);
            checkDigit(e.target.value) ? setHasDigit(true) : setHasDigit(false);
            checkSpecialCharacter(e.target.value) ? setHasSpecialCharacter(true) : setHasSpecialCharacter(false);
            
            // Vérifier concordance si confirmPassword existe
            if (formData.confirmPassword) {
                e.target.value === formData.confirmPassword ? setPasswordsMatch(true) : setPasswordsMatch(false);
            }
        }

        if (name === 'confirmPassword') {
            e.target.value === formData.newPassword ? setPasswordsMatch(true) : setPasswordsMatch(false);
        }
    };

    // Vérifier si le bouton doit être activé
    React.useEffect(() => {
        const isValid = hasGoodLength && 
                       hasCapitalLetter && 
                       hasLowercaseLetter && 
                       hasDigit && 
                       hasSpecialCharacter && 
                       passwordsMatch && 
                       formData.newPassword.length > 0 && 
                       formData.confirmPassword.length > 0;
        
        setEnabledValidateBtn(isValid);
    }, [hasGoodLength, hasCapitalLetter, hasLowercaseLetter, hasDigit, hasSpecialCharacter, passwordsMatch, formData.newPassword, formData.confirmPassword]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!enabledValidateBtn) {
            return;
        }

        setLoading(true);
        setErrors({ general: '' });

        try {
            const result = await createPasswordService.createPassword({
                token: formData.createToken,
                password: formData.newPassword,
                confirmPassword: formData.confirmPassword
            });

            if (result.success) {
                onSuccess && onSuccess();
            } else {
                setErrors({
                    general: result.message || 'Erreur lors de la création du mot de passe'
                });
            }
        } catch (error) {
            setErrors({
                general: 'Une erreur est survenue. Veuillez réessayer.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="create-password-form">
            <h1>Créé ton mot de passe</h1>
            <p>Pour accéder à ton espace client, créé maintenant ton mot de passe.</p>

            <div className='create-password-form__rules_container'>
                <div className='create-password-form__rule_container'>
                    <SvgIcons
                        name={hasGoodLength ? 'check_circle_fill' : 'empty_circle'}
                        width="20"
                        height="20"
                        color={hasGoodLength ? "#096e48" : "#36152F"}
                    />
                    <p className='create-password-form__rules_require'>Le mot de passe doit contenir entre 8 et 20 caractères</p>
                </div>
                <div className='create-password-form__rule_container'>
                    <SvgIcons
                        name={hasLowercaseLetter ? 'check_circle_fill' : 'empty_circle'}
                        width="20"
                        height="20"
                        color={hasLowercaseLetter ? "#096e48" : "#36152F"}
                    />
                    <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins une lettre minuscule</p>
                </div>
                <div className='create-password-form__rule_container'>
                    <SvgIcons
                        name={hasCapitalLetter ? 'check_circle_fill' : 'empty_circle'}
                        width="20"
                        height="20"
                        color={hasCapitalLetter ? "#096e48" : "#36152F"}
                    />
                    <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins une lettre majuscule</p>
                </div>
                <div className='create-password-form__rule_container'>
                    <SvgIcons
                        name={hasDigit ? 'check_circle_fill' : 'empty_circle'}
                        width="20"
                        height="20"
                        color={hasDigit ? "#096e48" : "#36152F"}
                    />
                    <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins un chiffre</p>
                </div>
                <div className='create-password-form__rule_container'>
                    <SvgIcons
                        name={hasSpecialCharacter ? 'check_circle_fill' : 'empty_circle'}
                        width="20"
                        height="20"
                        color={hasSpecialCharacter ? "#096e48" : "#36152F"}
                    />
                    <p className='create-password-form__rules_require'>Le mot de passe doit contenir au moins un caractère spécial</p>
                </div>
                <div className='create-password-form__rule_container'>
                    <SvgIcons
                        name={passwordsMatch ? 'check_circle_fill' : 'empty_circle'}
                        width="20"
                        height="20"
                        color={passwordsMatch ? "#096e48" : "#36152F"}
                    />
                    <p className='create-password-form__rules_require'>Les mots de passe sont identiques</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Nouveau mot de passe */}
                <div className="create-password-form__field">
                    <label htmlFor="newPassword">Nouveau mot de passe</label>
                    <div className="create-password-form__input-container">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name="newPassword"
                            value={formData.newPassword}
                            onChange={handleChange}
                            required
                            placeholder="Ton nouveau mot de passe"
                        />
                        <button
                            type="button"
                            tabIndex="-1"
                            onClick={togglePasswordVisibility}
                            className="create-password-form__toggle-password"
                            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                            <SvgIcons
                                name={showPassword ? 'eye' : 'eye-off'}
                                width="24"
                                height="24"
                                color="#36152F"
                            />
                        </button>
                    </div>
                </div>

                {/* Confirmation mot de passe */}
                <div className="create-password-form__field">
                    <label htmlFor="confirmPassword">Confirmation du mot de passe</label>
                    <div className="create-password-form__input-container">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            placeholder="Confirme ton mot de passe"
                        />
                        <button
                            type="button"
                            tabIndex="-1"
                            onClick={toggleConfirmPasswordVisibility}
                            className="create-password-form__toggle-password"
                            aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                            <SvgIcons
                                name={showConfirmPassword ? 'eye' : 'eye-off'}
                                width="24"
                                height="24"
                                color="#36152F"
                            />
                        </button>
                    </div>
                </div>

                {errors.general && (
                    <div className="create-password-form__error">
                        {errors.general}
                    </div>
                )}

                <button
                    type="submit"
                    className="create-password-form__submit"
                    disabled={loading || !enabledValidateBtn}
                >
                    {loading ? 'Création du mot de passe...' : 'Créer mon mot de passe'}
                </button>
            </form>
        </div>
    );
};

export default CreatePasswordForm;